// Eskom PARCC — gap-closure rollup (R3 #2 / 9 Jun alignment call).
//
// When a reviewer updates a finding's lifecycle status (Open → In Progress →
// Closed / Closed by Self-Declaration), the transaction-level compliance is
// RECOMPUTED from the finding set so the dashboard, reports and registers all
// agree — the propagation Lindiwe asked for ("reflect across the entire system
// wherever that transaction is referenced").
//
// Rules:
//   • Closed findings stop contributing their severity.
//   • Closed by Self-Declaration contributes the SELF-DECLARED variant of its
//     level (the buyer acknowledged the gap; it remains declared, not erased).
//   • Open / In Progress findings contribute their level as-is.
//   • Non-gap levels (Compliant / Not Applicable / Pending) never contribute.
//   • Contributions rank by the corrected tier weights (IE > Policy >
//     Housekeeping); at equal weight an OPEN gap outranks a self-declared one.
//   • Every gap closed → 'Compliant'. No findings at all → undefined.
import { riskWeight } from './dashboard';
import { coupleSelfDeclaredLevel } from './compliance';
import { normalizeFindingStatus } from './normalize';

/** The minimal finding shape the rollup reads (drafts, inputs and reads all fit). */
export interface RollupFinding {
  finalCompliance?: string | null;
  suggestedCompliance?: string | null;
  effectiveStatus?: string | null;
  legacyStatus?: string | null;
}

/** True when the level is a self-declared variant (used for tie-breaking). */
function isSelfDeclared(level: string): boolean {
  return /^self-declared:/i.test(level);
}

/**
 * Recompute the transaction-level compliance from its findings' lifecycle
 * states. Returns undefined when there are no findings (no signal — leave the
 * stored level alone).
 */
export function rollupComplianceFromFindings(
  findings: readonly RollupFinding[],
): string | undefined {
  if (findings.length === 0) return undefined;

  const contributions: string[] = [];
  for (const f of findings) {
    const level = (f.finalCompliance ?? f.suggestedCompliance ?? '').trim();
    if (!level || riskWeight(level) === 0) continue; // not a gap level
    const status = normalizeFindingStatus(f.effectiveStatus ?? f.legacyStatus ?? '');
    if (status === 'Closed') continue; // resolved — no longer contributes
    if (status === 'Closed by Self-Declaration') {
      contributions.push(coupleSelfDeclaredLevel(level));
      continue;
    }
    contributions.push(level); // Open / In Progress / unset → live gap
  }

  if (contributions.length === 0) return 'Compliant';
  // Most severe wins; at equal weight, an open (plain) gap beats a self-declared one.
  contributions.sort((a, b) => {
    const byWeight = riskWeight(b) - riskWeight(a);
    if (byWeight !== 0) return byWeight;
    return Number(isSelfDeclared(a)) - Number(isSelfDeclared(b));
  });
  return contributions[0];
}

/**
 * Roll up a transaction's GAP STATUS from its findings' lifecycle, onto the
 * four-state filter vocabulary and the dashboard's open/closed split, so the
 * persisted `Transaction.gapsStatus` matches the findings (and can be filtered on):
 *   • no gap findings     → '' (nothing to report — not a fifth state)
 *   • any gap still open  → 'Open', or 'In Progress' when some are also closed
 *   • every gap closed    → 'Closed', or 'Closed by Self-Declaration' when every
 *                           closure was a self-declaration
 * A finding is a gap when its level carries a risk weight; "Additional comments"
 * notes are excluded (they are not findings). A gap is closed when its status is
 * Closed / Closed-by-Self-Declaration OR its level is a self-declared variant —
 * the same closure rule the dashboard's Gaps Closed uses.
 */
export function rollupGapStatusFromFindings(findings: readonly RollupFinding[]): string {
  let open = 0;
  let closed = 0;
  let closedSelfDeclared = 0;
  for (const f of findings) {
    if (f.legacyStatus === 'Additional comments') continue;
    const level = (f.finalCompliance ?? f.suggestedCompliance ?? '').trim();
    if (!level || riskWeight(level) === 0) continue; // not a gap
    const status = normalizeFindingStatus(f.effectiveStatus ?? f.legacyStatus ?? '');
    const selfDeclared = status === 'Closed by Self-Declaration' || isSelfDeclared(level);
    if (status === 'Closed' || selfDeclared) {
      closed += 1;
      if (selfDeclared) closedSelfDeclared += 1;
    } else {
      open += 1;
    }
  }
  if (open === 0 && closed === 0) return '';
  if (open > 0 && closed > 0) return 'In Progress';
  if (open > 0) return 'Open';
  return closedSelfDeclared === closed ? 'Closed by Self-Declaration' : 'Closed';
}
